import React from "react";
import { motion } from "framer-motion";

const ReadinessScoreGraph: React.FC = () => {
    const readinessScore = 85;
    const hrv = 65;
    const sleepQuality = 87;
    const activityLoad = 78;

    const getReadinessStatus = (score: number) => {
        if (score >= 85) return { text: "عالی", color: "#32D74B", advice: "زمان مناسبی برای چالش‌های جدید است." };
        if (score >= 70) return { text: "خوب", color: "#66D4CF", advice: "می‌توانید فعالیت‌های معمول را انجام دهید." };
        if (score >= 50) return { text: "متوسط", color: "#FFD60A", advice: "امروز به استراحت بیشتری نیاز دارید." };
        return { text: "ضعیف", color: "#FF3B30", advice: "روز مناسبی برای استراحت کامل است." };
    };

    const status = getReadinessStatus(readinessScore);

    return (
        <div className="space-y-3" dir="rtl">
            <div>
                <h4 className="font-medium text-[#002B5C] mb-3">امتیاز آمادگی امروز</h4>

                {/* Circular Progress */}
                <div className="relative flex items-center justify-center mb-6">
                    <svg className="w-40 h-40 transform -rotate-90">
                        <circle cx="80" cy="80" r="70" stroke="#ECEFF1" strokeWidth="12" fill="none" />
                        <motion.circle
                            cx="80"
                            cy="80"
                            r="70"
                            stroke={status.color}
                            strokeWidth="12"
                            fill="none"
                            strokeLinecap="round"
                            initial={{ strokeDasharray: "0 440" }}
                            animate={{ strokeDasharray: `${(readinessScore / 100) * 440} 440` }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <motion.div className="text-4xl font-bold" style={{ color: status.color }} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, delay: 0.3 }}>
                            {readinessScore}٪
                        </motion.div>
                        <div className="text-sm text-[#9EA3A9] mt-1">{status.text}</div>
                    </div>
                </div>

                {/* Advice Box */}
                <div className="p-3 bg-[#ECEFF1]/50 rounded-[16px] mb-3 border border-[#002B5C]/10">
                    <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-[#0072CE] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            />
                        </svg>
                        <div className="text-sm text-[#4A5568]">{status.advice}</div>
                    </div>
                </div>

                {/* Contributing Factors */}
                <h4 className="font-medium text-[#002B5C] mb-2">عوامل تاثیرگذار</h4>
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-[#4A5568]">HRV (تغییرپذیری ضربان قلب)</span>
                        <span className="text-sm font-medium text-[#0072CE]">{hrv} ms</span>
                    </div>
                    <div className="w-full bg-[#ECEFF1] rounded-full h-2">
                        <motion.div
                            className="h-2 rounded-full bg-gradient-to-r from-[#0072CE] to-[#00E5FF]"
                            initial={{ width: 0 }}
                            animate={{ width: `${(hrv / 100) * 100}%` }}
                            transition={{ duration: 1, delay: 0.2 }}
                        />
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <span className="text-sm text-[#4A5568]">کیفیت خواب شب گذشته</span>
                        <span className="text-sm font-medium text-[#0072CE]">{sleepQuality}٪</span>
                    </div>
                    <div className="w-full bg-[#ECEFF1] rounded-full h-2">
                        <motion.div
                            className="h-2 rounded-full bg-gradient-to-r from-[#A7FFEB] to-[#66D4CF]"
                            initial={{ width: 0 }}
                            animate={{ width: `${sleepQuality}%` }}
                            transition={{ duration: 1, delay: 0.4 }}
                        />
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <span className="text-sm text-[#4A5568]">بار فعالیت روز قبل</span>
                        <span className="text-sm font-medium text-[#0072CE]">{activityLoad}٪</span>
                    </div>
                    <div className="w-full bg-[#ECEFF1] rounded-full h-2">
                        <motion.div
                            className="h-2 rounded-full bg-gradient-to-r from-[#32D74B] to-[#66D4CF]"
                            initial={{ width: 0 }}
                            animate={{ width: `${activityLoad}%` }}
                            transition={{ duration: 1, delay: 0.6 }}
                        />
                    </div>
                </div>

                {/* Weekly Trend */}
                <h4 className="font-medium text-[#002B5C] mt-4 mb-2">روند هفتگی</h4>
                <div className="h-20 bg-[#ECEFF1] rounded-[16px] p-3 flex items-end justify-between shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    {[72, 85, 78, 82, 90, 75, 85].map((value, i) => (
                        <div key={i} className="flex flex-col items-center">
                            <motion.div
                                className="w-6 rounded-t-[4px]"
                                style={{
                                    backgroundColor: value >= 85 ? "#32D74B" : value >= 70 ? "#66D4CF" : value >= 50 ? "#FFD60A" : "#FF3B30",
                                }}
                                initial={{ height: 0 }}
                                animate={{ height: `${value}%` }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                            />
                            <span className="text-xs text-[#9EA3A9] mt-1">{["ش", "ی", "د", "س", "چ", "پ", "ج"][i]}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ReadinessScoreGraph;
